import { GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';

import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { BankIcon } from '@/components/icons/BankIcon';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { DataTableProps } from '@/components/tables/DataTable/types';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { EditLiabilitySideDrawer } from '@/modules/liabilities/LiabilityModal/EditLiabilitySideDrawer';
import { COLORS } from '@/styles/tokens/colors';

import { CreateLiabilityButton } from '../CreateLiabilityButton';
import { LiabilitiesTable_LiabilityFragment } from './graphql/LiabilitiesTable.generated';
import { LiabilitiesTableRowData } from './LiabilitiesTable.constants';
import { mapLiabilitiesToRows, useColumns } from './LiabilitiesTable.utils';

export interface LiabilitiesTableProps {
  householdId: string;
  tableProps: Partial<DataTableProps>;
  loading: boolean;
  data: LiabilitiesTable_LiabilityFragment[];
  updateTable: () => void;
}

function LiabilitiesTableEmptyState() {
  return (
    <TableEmptyState text="No existing liabilities">
      <CreateLiabilityButton />
    </TableEmptyState>
  );
}

export function LiabilitiesTable({
  householdId,
  tableProps,
  loading,
  data,
  updateTable,
}: LiabilitiesTableProps) {
  const [editingLiabilityId, setEditingLiabilityId] = useState<string | null>(
    null
  );
  const canEdit = useCanEditHouseholdData();

  const rows = useMemo(
    () => mapLiabilitiesToRows(data ?? [], householdId),
    [data, householdId]
  );

  const columns = useColumns({
    refetch: updateTable,
    hidePaymentColumns: false,
  });

  if (rows.length === 0 && !loading) {
    return (
      <SmallTableEmptyState
        fill="viewport"
        icon={<BankIcon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="No liabilities"
        description="Liabilities will impact current entity valuations and future projections in the estate waterfall"
        action={<CreateLiabilityButton />}
      />
    );
  }

  return (
    <>
      {editingLiabilityId && (
        <EditLiabilitySideDrawer
          onClose={() => {
            setEditingLiabilityId(null);
            void updateTable();
          }}
          isOpen={editingLiabilityId !== null}
          liabilityId={editingLiabilityId}
        />
      )}
      <DataTable
        loading={loading}
        {...tableProps}
        rows={rows}
        columns={columns}
        slots={{
          noRowsOverlay: LiabilitiesTableEmptyState,
        }}
        onRowClick={
          canEdit
            ? ({ row }: GridRowParams<LiabilitiesTableRowData>) => {
                setEditingLiabilityId(row.id);
              }
            : undefined
        }
        noPointer={!canEdit}
        rowHoverVariant={canEdit ? 'default' : 'transparent'}
      />
    </>
  );
}
